import { jiraController, fetchIsWhiteListed } from "../api/jira.api";
import IconComponent from "@CubeComponents/icons";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { CircularProgress, Menu, MenuItem, Tooltip } from "@mui/material";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@CubeComponents/html/accordion/materialUiAccordian/index";
import { Trans, useTranslation, untranslated } from "@i18n";
import _ from "lodash";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import { convertDate } from "@Services/luxonHelper";
import { convertHtmlToAdf, mutedStatusColors } from "./constants";
import { replaceBase64WithTempKeys, extractTempKeysFromHtml } from "@Helpers/htmlUtils";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import TabBarComponent from "@CubeComponents/form/TabBarComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { fetchUserTagData } from "@Actions/setUserTagData";
import {
  recordStatus,
  severityDataSource,
  severityDataSourceCustomSla,
  getJiraStatusHex,
  issueTypeIdToLabel,
  jiraIdMapper,
  statusCategoryMapper,
  errorCodes,
} from "@Helpers";
import { getNoAccessPage } from "@Features/estimation";
import { getBackendUrl } from "@Utils";
import type { JiraControllerResponse } from "@ServerTypes/jira/jira.schema";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import ChangeTicketTypeModal, { ChangeTypePayload } from "./ChangeTicketTypeModal";
import { issueTypeIcons } from "./useJiraTickets";
import { SlaSection } from "./sla/SlaSection";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import Box from "@mui/material/Box";
import Text from "@CubeComponents/html/Text/Text";
import { SimpleEditor } from "@CubeComponents/RichTextEditor/components/tiptap-templates/simple/ControlledSimpleEditor";
import { sanitizeHtml } from "@Utils/sanitize";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { TranslatedString } from "@i18n";
import type { Sla } from "@ServerTypes/sla/sla.schema";
import type { SelectDropDownProps } from "@CubeComponents/Mui_Form/SelectDropDown";
import type { SlaReportTicket } from "./sla/slaReportModel";

/** An `{ id, label }` pair as the ticket document stores it (status, impact, severity, priority). */
type JiraLabeledId = { id?: string; label?: string };

/** One option of the status / severity / type `SelectDropDown`s. `id` is always present because every
 *  one of those option lists is built from an id-keyed map in `@Helpers/jiraConstants`. */
type JiraOption = { id: string; label?: string };

/** One entry of `getIssueTransitions`' result: `buildTransition()`'s `{ id, label, issueStatusCategory }`
 *  plus the two extra fields `jira.service.ts` adds when the destination has an auto-comment. */
type JiraTransitionOption = JiraOption & {
  label: string;
  issueStatusCategory?: JiraLabeledId;
  requireConfirmation?: boolean;
  prefillComment?: string;
};

/** A file reference on the ticket or on one comment. The stored records also carry `mimeType` and
 *  `size`; only these two are read here. */
type JiraAttachmentRef = { fileName: string; fileId: string };

type JiraActivityUser = { _id?: string; fullName?: string };

/**
 * One entry of `jiraTickets.activity[]`. The server writes four kinds of entry into that single array
 * — `comment`, `status`, `severity` and `typeChange` — so every kind-specific field is optional here;
 * `type` and `createdOn` are the only two that every entry carries.
 */
type JiraActivity = {
  type: string;
  createdOn: number;
  user?: JiraActivityUser;
  // comment entries
  commentId?: string;
  comment?: string;
  isInternalNote?: boolean;
  replyToId?: string;
  editedOn?: number;
  recordStatus?: number;
  reactions?: JiraActivityUser[];
  attachments?: JiraAttachmentRef[];
  // status and typeChange entries
  fromStatusId?: string;
  fromStatusName?: string;
  toStatusId?: string;
  toStatusName?: string;
  fromTypeId?: string;
  fromTypeName?: string;
  toTypeId?: string;
  toTypeName?: string;
  // severity and typeChange entries
  fromImpact?: JiraLabeledId;
  toImpact?: JiraLabeledId;
  fromSeverity?: JiraLabeledId;
  toSeverity?: JiraLabeledId;
  fromPriority?: JiraLabeledId;
  toPriority?: JiraLabeledId;
};

/** A `comment` activity entry — reached only through the `type === "comment"` filter in
 *  `renderCommentsList`, which is where `commentId`/`comment` become known-present. */
type JiraCommentActivity = JiraActivity & { commentId: string; comment: string };

/**
 * The ticket as `POST /jira/getTicketDetails` returns it. SCHEMA GAP: `jiraControllerResponse` is the
 * opaque `statusResponse`, so no ticket shape is declared server-side. This is `SlaReportTicket` (the
 * SLA reports' view of the same document) widened with the fields this page reads. Everything stays
 * optional because the state starts as `{}` until the first fetch lands.
 */
type JiraTicketDetail = SlaReportTicket & {
  activity?: JiraActivity[];
  attachments?: JiraAttachmentRef[];
  cubeDevTicketKey?: string;
  cubeDevTicketStatus?: { name?: string };
  organization?: { name?: string; visibility?: boolean };
  portal?: { frontendUrl?: string; visibility?: boolean };
  fields?: {
    summary?: string;
    description?: string;
    link?: string;
    // `SlaReportTicket` declares these three by label only — the reports never read their ids, this
    // page does — and its `sla` by the handful of clock fields the reports read, not the full record.
    issueType?: JiraLabeledId;
    issueStatus?: JiraLabeledId;
    issueStatusCategory?: JiraLabeledId;
    priority?: JiraLabeledId;
    sla?: Sla;
  };
};

/** `SelectDropDown`'s `onChange` is declared for the multi-select case (`T | T[] | null`). None of the
 *  three dropdowns here sets `multiple` or `clearable`, so each handler is only ever called with one
 *  option — asserted at the prop rather than widening each handler body to a union it never sees. */
type SingleSelectHandler<T extends Record<string, unknown>> = SelectDropDownProps<T>["onChange"];

/** The `jiraController` axios response widened with the payload a given handler really returns.
 *  SCHEMA GAP as above — neither `result` nor `errorCode` is declared on `statusResponse`. */
type JiraResponse<TData> = Awaited<ReturnType<typeof jiraController>> & { data: TData };

/** What `uploadFiles` resolves to — either the real `postCommentAttachment` response or the local
 *  no-files stub, so it is narrower than a full axios response. */
type UploadFilesResult = { data: JiraControllerResponse & { result?: JiraAttachmentRef[] } };

const isHtmlContentEmpty = (html: string) => {
  if (!html) return true;
  if (/<(img|video|iframe)\b/i.test(html)) return false;
  return (
    html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .trim().length === 0
  );
};

const ActivityStatusChip = ({ statusId, children }: { statusId?: string; children?: React.ReactNode }) => {
  const { bg, border } = mutedStatusColors(getJiraStatusHex(statusId, statusCategoryMapper()[statusId ?? ""]?.id));
  return (
    <Box
      component="span"
      className="inline-flex items-center rounded px-2 py-1 mx-2 text-xs font-semibold"
      sx={{ backgroundColor: bg, color: "#192048", border: `1px solid ${border}` }}
    >
      {children}
    </Box>
  );
};

const ActivityTypeChip = ({ typeId, children }: { typeId?: string; children?: React.ReactNode }) => (
  <span className="inline-flex items-center gap-1 font-semibold align-middle">
    {typeId && issueTypeIcons[typeId] && <IconComponent name={issueTypeIcons[typeId]} width="1rem" height="1rem" />}
    {children}
  </span>
);

const TicketDetails = ({ ticketId }: { ticketId: string }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  // KNOWN BUG, preserved verbatim: this ref is never attached to anything (no `ref=` prop passes it),
  // so `.current` is permanently null and the `destroy()` in handleCloseCommentEditor never runs.
  const richTextEditorRef = useRef<{ destroy: () => void } | null>(null);
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const portalSettingInfo = useSelector((state: RootState) => state.portalSettingInfo);

  const [ticketData, setTicketData] = useState<JiraTicketDetail>({});
  const [accessError, setAccessError] = useState<number | null>(null);
  const [showCommentEditor, setShowCommentEditor] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<{
    visibleToPortalUsers: boolean;
    visibleToOrgUsers: boolean;
    comment: boolean;
    transition: boolean;
    severity: boolean;
  }>({
    visibleToPortalUsers: false,
    visibleToOrgUsers: false,
    comment: false,
    transition: false,
    severity: false,
  });
  const [isInternalNote, setIsInternalNote] = useState<boolean>(false);
  const [isCurrentUserWhiteListed, setIsCurrentUserWhiteListed] = useState<boolean>(false);
  const [commentFiles, setCommentFiles] = useState<File[]>([]);
  const [attachmentsViewState, setAttachmentsViewState] = useState<{
    state: boolean;
    attachments: { fileName: string; fileId: string }[];
  }>({
    state: false,
    attachments: [],
  });
  const [issueTransitions, setIssueTransitions] = useState<JiraTransitionOption[]>([]);
  const [commentValue, setCommentValue] = useState<string>("");
  const [replyingToCommentId, setReplyingToCommentId] = useState<string | null>(null);
  const [replyValue, setReplyValue] = useState<string>("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentValue, setEditCommentValue] = useState<string>("");
  const [editCommentExistingAttachments, setEditCommentExistingAttachments] = useState<
    { fileName: string; fileId: string }[]
  >([]);
  const [editCommentPendingFiles, setEditCommentPendingFiles] = useState<File[]>([]);
  const [deleteConfirmCommentId, setDeleteConfirmCommentId] = useState<string | null>(null);
  const [replyingToIsInternalNote, setReplyingToIsInternalNote] = useState<boolean>(false);
  const [showChangeTypeModal, setShowChangeTypeModal] = useState<boolean>(false);
  const [changeTypeTargetId, setChangeTypeTargetId] = useState<string>("");
  const [changeTypeLoading, setChangeTypeLoading] = useState<boolean>(false);
  const [commentMenuAnchor, setCommentMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuComment, setMenuComment] = useState<JiraCommentActivity | null>(null);
  const [replyFiles, setReplyFiles] = useState<File[]>([]);
  const [openSidebarSections, setOpenSidebarSections] = useState<string[] | null>(null);
  const [confirmTransition, setConfirmTransition] = useState<string | null>(null);
  const [confirmComment, setConfirmComment] = useState<string>("");
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [confirmSeverityChange, setConfirmSeverityChange] = useState<JiraOption | null>(null);
  const [severityJustification, setSeverityJustification] = useState<string>("");
  const [severityJustificationLoading, setSeverityJustificationLoading] = useState<boolean>(false);

  const canViewSla = isCurrentUserWhiteListed || Boolean(portalSettingInfo?.viewSupportSla?.enable);

  const isTicketCreator =
    userDetails?._id && _.get(ticketData, "createdBy._id")
      ? String(userDetails._id) === String(_.get(ticketData, "createdBy._id"))
      : false;
  const canManageVisibility = isCurrentUserWhiteListed || isTicketCreator;
  const customSupportSlaEnabled = Boolean(_.get(ticketData, "portal.customSupportSla"));
  // Compared by id, not by the stored label: the label is display text that only the backend is
  // allowed to write, and gating layout on it makes the page depend on its exact wording.
  const isBugTicket = _.get(ticketData, "fields.issueType.id") === jiraIdMapper["Report a Bug"];

  const fetchTicketData = useCallback(async () => {
    const res = (await jiraController({ value: { action: "getTicketDetails", ticketId: ticketId } })) as JiraResponse<{
      result: JiraTicketDetail;
      errorCode?: number;
    }>;
    if (res.data.status) {
      setTicketData(res.data.result);
      setAccessError(null);
    } else {
      setAccessError(res.data.errorCode ?? errorCodes.JIRA_TICKET_NO_ACCESS);
    }
  }, [ticketId]);

  const loadIsWhiteListed = async () => {
    setIsCurrentUserWhiteListed(await fetchIsWhiteListed());
  };

  const fetchIssueTransitions = useCallback(async () => {
    const res = (await jiraController({
      value: {
        action: "getIssueTransitions",
        ticketId,
      },
    })) as JiraResponse<{ result: JiraTransitionOption[] }>;
    if (res.data.status) {
      setIssueTransitions(res.data.result);
    }
  }, [ticketId]);

  useEffect(() => {
    loadIsWhiteListed();
    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.id === "commentImageOpenIcon") {
        const imageContainer = target.closest("[data-attachment-url]");
        const imageUrl = imageContainer?.getAttribute("data-attachment-url");

        if (imageUrl) {
          globalThis.open(imageUrl, "_blank");
        }
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, []);

  const ticketStatusId = _.get(ticketData, "fields.issueStatus.id");
  useEffect(() => {
    fetchTicketData();
  }, [ticketStatusId, fetchTicketData]);

  useEffect(() => {
    if (ticketId && isCurrentUserWhiteListed) {
      fetchIssueTransitions();
    }
  }, [ticketStatusId, ticketId, userDetails?.email, isCurrentUserWhiteListed, fetchIssueTransitions]);

  useEffect(() => {
    const activity = ticketData?.activity || [];
    const seen = new Set<string>();
    for (const item of activity) {
      // `ticketData` is untyped (any): the activity user's _id enters the type system here.
      const id = item?.user?._id ? (String(item.user._id) as ObjectIdString) : "";
      if (id && !seen.has(id)) {
        seen.add(id);
        dispatch(fetchUserTagData(id));
      }
    }
  }, [ticketData?.activity, dispatch]);

  const handleTransitionChange = async (option: JiraTransitionOption) => {
    if (!option) return;
    if (option.requireConfirmation) {
      setConfirmTransition(option.id);
      const reporterName = _.get(ticketData, "createdBy.fullName") || "";
      const body = option.prefillComment ? `<p>${option.prefillComment}</p>` : "";
      setConfirmComment(`<p>Hello ${_.escape(reporterName)},</p>${body}<p>Regards,<br/>CUBE Support Team</p>`);
      return;
    }
    setShowLoading((prev) => ({ ...prev, transition: true }));
    const res = await jiraController({
      value: {
        ticketId,
        statusId: option.id,
        action: "changeTransition",
      },
    });
    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t("Status changed successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      await Promise.all([fetchTicketData(), fetchIssueTransitions()]);
    }
    setShowLoading((prev) => ({ ...prev, transition: false }));
  };

  const submitConfirmTransition = async () => {
    if (!confirmTransition || isHtmlContentEmpty(confirmComment)) return;
    setConfirmLoading(true);
    const res = await jiraController({
      value: {
        ticketId,
        statusId: confirmTransition,
        transitionComment: confirmComment,
        action: "changeTransition",
      },
    });
    setConfirmLoading(false);
    if (res.data.status) {
      dispatch(showToastNotification({ content: t("Status changed successfully"), type: TOAST_TYPE_SUCCESS }));
      setConfirmTransition(null);
      setConfirmComment("");
      await Promise.all([fetchTicketData(), fetchIssueTransitions()]);
    } else {
      dispatch(
        showToastNotification({
          content: res.data.errorMessage ? untranslated(res.data.errorMessage) : t("Failed to change status"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const jiraTicketStatusTemplate = (statusCategory: JiraLabeledId, status: JiraLabeledId) => {
    const { bg, border } = mutedStatusColors(getJiraStatusHex(status?.id, statusCategory?.id));
    if (isCurrentUserWhiteListed) {
      return (
        <SelectDropDown<JiraTransitionOption>
          options={issueTransitions}
          fields={{ text: "label", value: "id" }}
          // The stored `fields.issueStatus` is only `{ id, label }` — narrower than a transition
          // option, which the dropdown never needs it to be, since it only displays the current value.
          value={(_.get(ticketData, "fields.issueStatus") || null) as JiraTransitionOption | null}
          placeholder={_.get(ticketData, "fields.issueStatus.label")}
          isLoading={showLoading.transition}
          disabled={showLoading.transition}
          onChange={handleTransitionChange as SingleSelectHandler<JiraTransitionOption>}
          renderOption={(option: JiraTransitionOption) => {
            const optionColors = mutedStatusColors(getJiraStatusHex(option.id, option.issueStatusCategory?.id));
            return (
              <Box
                sx={{ backgroundColor: optionColors.bg, color: "#192048", border: `1px solid ${optionColors.border}` }}
                className="font-semibold px-3 py-1 rounded text-sm w-fit whitespace-nowrap"
              >
                {option.label}
              </Box>
            );
          }}
          sx={{
            background: bg,
            color: "#192048",
            border: `1px solid ${border}`,
            "&:hover": { background: bg },
          }}
        />
      );
    }
    return (
      <Box
        sx={{ backgroundColor: bg, color: "#192048", border: `1px solid ${border}` }}
        className="font-semibold px-4 py-2 rounded whitespace-nowrap w-fit items-center"
      >
        {status.label}
      </Box>
    );
  };

  const submitSeverityUpdate = async (option: JiraOption, justification?: string) => {
    setShowLoading((prev) => ({ ...prev, severity: true }));
    const response = await jiraController({
      value: {
        action: "updateSeverity",
        ticketId,
        severity: option.id,
        ...(justification ? { justification } : {}),
      },
    });
    if (response.data.status) {
      await fetchTicketData();
      dispatch(
        showToastNotification({
          content: t("Severity updated successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage
            ? untranslated(response.data.errorMessage)
            : t("Failed to update severity"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setShowLoading((prev) => ({ ...prev, severity: false }));
  };

  const handleSeverityChange = async (option: JiraOption) => {
    if (!option) return;
    if (customSupportSlaEnabled) {
      setConfirmSeverityChange({ id: option.id, label: option.label });
      setSeverityJustification(`<p>${t("Reason for changing severity to")} ${_.escape(option.label)}: </p>`);
      return;
    }
    await submitSeverityUpdate(option);
  };

  const submitConfirmSeverityChange = async () => {
    if (!confirmSeverityChange || isHtmlContentEmpty(severityJustification)) return;
    setSeverityJustificationLoading(true);
    await submitSeverityUpdate(confirmSeverityChange, severityJustification);
    setSeverityJustificationLoading(false);
    setConfirmSeverityChange(null);
    setSeverityJustification("");
  };

  const severityTemplate = () => {
    if (!isCurrentUserWhiteListed) return _.get(ticketData, "fields.severity.label") || "-";
    return (
      <SelectDropDown<JiraOption>
        options={customSupportSlaEnabled ? severityDataSourceCustomSla(t) : severityDataSource(t)}
        fields={{ text: "label", value: "id" }}
        value={(_.get(ticketData, "fields.severity") || null) as JiraOption | null}
        placeholder={t("Select Severity")}
        isLoading={showLoading.severity}
        buttonType={BUTTON_TYPES.BORDER_BUTTON}
        onChange={handleSeverityChange as SingleSelectHandler<JiraOption>}
      />
    );
  };

  const handleTypeChange = (option: JiraOption) => {
    if (!option || option.id === _.get(ticketData, "fields.issueType.id")) return;
    setChangeTypeTargetId(option.id);
    setShowChangeTypeModal(true);
  };

  const handleChangeTypeConfirm = async (payload: ChangeTypePayload) => {
    setChangeTypeLoading(true);
    const res = await jiraController({
      value: {
        action: "changeType",
        ticketId: payload.ticketId,
        newIssueType: payload.newIssueType,
        newStatus: payload.newStatus,
        ...(payload.impact ? { impact: payload.impact } : {}),
        ...(payload.severity ? { severity: payload.severity } : {}),
      },
    });
    setChangeTypeLoading(false);
    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t("Ticket type changed successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      setShowChangeTypeModal(false);
      await fetchTicketData();
    } else {
      dispatch(
        showToastNotification({
          content: res.data.errorMessage ? untranslated(res.data.errorMessage) : t("Failed to change ticket type"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const typeTemplate = () => {
    const currentType = (_.get(ticketData, "fields.issueType") || null) as JiraOption | null;
    if (!isCurrentUserWhiteListed) {
      return (
        <Box className="flex items-center gap-2">
          {currentType?.id && <IconComponent name={issueTypeIcons[currentType.id]} width="1.25rem" height="1.25rem" />}
          {currentType?.label || "-"}
        </Box>
      );
    }
    const labelMap = issueTypeIdToLabel(t);
    const typeOptions = Object.keys(labelMap)
      .filter((id) => id !== currentType?.id)
      .map((id) => ({ id, label: labelMap[id] }));
    return (
      <SelectDropDown<JiraOption>
        options={typeOptions}
        fields={{ text: "label", value: "id" }}
        value={currentType}
        placeholder={t("Select Type")}
        buttonType={BUTTON_TYPES.BORDER_BUTTON}
        startIcon={
          currentType?.id ? (
            <IconComponent name={issueTypeIcons[currentType.id]} width="1.25rem" height="1.25rem" />
          ) : null
        }
        renderOption={(option: JiraOption) => (
          <Box className="flex gap-2 items-center">
            <IconComponent name={issueTypeIcons[option.id]} width="1.25rem" height="1.25rem" />
            <Text>{option.label}</Text>
          </Box>
        )}
        onChange={handleTypeChange as SingleSelectHandler<JiraOption>}
      />
    );
  };

  const detailsMapper = [
    {
      id: "createdBy",
      name: t("Created By"),
      value: (
        <UserNameTagComponent
          id={_.get(ticketData, "createdBy._id")}
          fullName={_.get(ticketData, "createdBy.fullName")}
          email={_.get(ticketData, "createdBy.email")}
        />
      ),
    },
    {
      id: "createdOn",
      name: t("Created On"),
      value: convertDate(_.get(ticketData, "createdOn"), "dateForTooltip"),
    },
    {
      id: "type",
      name: t("Type"),
      value: typeTemplate(),
    },
    {
      id: "status",
      name: t("Status"),
      value: jiraTicketStatusTemplate(
        _.get(ticketData, "fields.issueStatusCategory", {}),
        _.get(ticketData, "fields.issueStatus", {}),
      ),
    },
    ...(isBugTicket && !customSupportSlaEnabled
      ? [
          {
            id: "impact",
            name: t("Impact"),
            value: _.get(ticketData, "fields.impact.label") || "-",
          },
        ]
      : []),
    ...((canViewSla || customSupportSlaEnabled) && isBugTicket
      ? [
          {
            id: "severity",
            name: t("Severity"),
            value: severityTemplate(),
          },
        ]
      : []),
    ...(canViewSla && isBugTicket && !customSupportSlaEnabled
      ? [
          {
            id: "priority",
            name: t("Priority"),
            value: _.get(ticketData, "fields.priority.label") || "-",
          },
        ]
      : []),
    ...(_.get(ticketData, "fields.link")
      ? [
          {
            id: "url",
            name: t("URL"),
            value: (
              <a
                className="text-blue-600 underline text-sm"
                target="_blank"
                rel="noreferrer"
                href={_.get(ticketData, "fields.link")}
              >
                {_.get(ticketData, "fields.link")}
              </a>
            ),
          },
        ]
      : []),
    ...(isCurrentUserWhiteListed
      ? [
          {
            id: "portalName",
            name: t("Portal Name"),
            value:
              _.get(ticketData, "portal.name") && _.get(ticketData, "portal.frontendUrl")
                ? `${_.get(ticketData, "portal.name")} (${_.get(ticketData, "portal.frontendUrl")})`
                : _.get(ticketData, "portal.name") || _.get(ticketData, "portal.frontendUrl") || "-",
          },
          {
            id: "orgName",
            name: t("Organization"),
            value: _.get(ticketData, "organization.name") || "-",
          },
        ]
      : []),
    ...(isCurrentUserWhiteListed
      ? [
          ...(ticketData?.cubeDevTicketKey
            ? [
                {
                  id: "cubeDevTicket",
                  name: t("Linked CUBE Ticket"),
                  value: (
                    <span className="text-sm">
                      <a
                        className="text-blue-600 underline"
                        target="_blank"
                        rel="noreferrer"
                        href={`https://cube-inc.atlassian.net/browse/${ticketData.cubeDevTicketKey}`}
                      >
                        {ticketData.cubeDevTicketKey}
                      </a>
                      {ticketData?.cubeDevTicketStatus?.name ? ` - ${ticketData.cubeDevTicketStatus.name}` : ""}
                    </span>
                  ),
                },
              ]
            : []),
        ]
      : []),
  ];

  const sideBarHeader = ({ icon, name }: { icon: string; name: string }) => (
    <Box className="flex gap-4 items-center">
      <IconComponent name={icon} />
      <Box className="font-semibold text-lg">{name}</Box>
    </Box>
  );

  const handleVisibiltyChange = async (
    args: React.ChangeEvent<HTMLInputElement>,
    key: "visibleToPortalUsers" | "visibleToOrgUsers",
  ) => {
    const loadingState: { visibleToPortalUsers?: boolean; visibleToOrgUsers?: boolean } = {
      [key]: true,
    };
    if (key == "visibleToPortalUsers" && args.target.checked) {
      loadingState.visibleToOrgUsers = true;
    } else if (key == "visibleToOrgUsers" && !args.target.checked) {
      loadingState.visibleToPortalUsers = true;
    }
    setShowLoading((prev) => ({ ...prev, ...loadingState }));
    const response = await jiraController({
      value: {
        action: "updateVisibility",
        field: {
          key,
          value: args.target.checked,
        },
        ticketId,
      },
    });
    if (response.data.status) {
      const updatedPortal = { ...ticketData.portal };
      const updatedOrg = { ...ticketData.organization };

      if (key == "visibleToPortalUsers") {
        updatedPortal.visibility = args.target.checked;
        if (args.target.checked) {
          updatedOrg.visibility = true;
        }
      } else if (key == "visibleToOrgUsers") {
        updatedOrg.visibility = args.target.checked;
        if (!args.target.checked) {
          updatedPortal.visibility = false;
        }
      }
      setTicketData((prev) => ({
        ...prev,
        portal: updatedPortal,
        organization: updatedOrg,
      }));
      dispatch(
        showToastNotification({
          content: t("Visibility updated successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    setShowLoading((prev) => ({
      ...prev,
      visibleToPortalUsers: false,
      visibleToOrgUsers: false,
    }));
  };

  const sideBarContent = ({ id }: { id: string }) => {
    if (id == "sla") {
      return (
        <SlaSection
          sla={_.get(ticketData, "fields.sla")}
          useWallClock={customSupportSlaEnabled}
          onRefresh={
            isCurrentUserWhiteListed
              ? async () => {
                  await jiraController({ value: { action: "recomputeSla", ticketId } });
                  await fetchTicketData();
                }
              : undefined
          }
        />
      );
    }
    if (id == "details") {
      return (
        <Box className="flex flex-col gap-5 pe-4">
          {detailsMapper.map((data, index: number) => (
            <Box key={index} className="flex items-center w-full">
              <Box className="flex-grow min-w-0 basis-1/3 flex-1 font-semibold text-sm">{data.name}</Box>
              <Box className="flex-grow min-w-0 basis-2/3 break-words flex-2">
                {typeof data.value === "string" ? <span className="text-sm">{data.value}</span> : data.value}
              </Box>
            </Box>
          ))}
        </Box>
      );
    } else if (id == "attachments") {
      if ((ticketData.attachments || []).length) {
        return (
          <Box className="flex flex-wrap gap-2">
            {(ticketData.attachments || []).map((attachment, index: number) => (
              <a
                key={index}
                href={`${getBackendUrl()}/jira/getAttachmentContent/${attachment.fileId}`}
                target="_blank"
                aria-label={attachment.fileName}
                rel="noreferrer"
                className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-white text-[#192048] text-sm font-semibold cursor-pointer transition-colors hover:bg-light_background"
                style={{ borderColor: "#d1dbeb" }}
              >
                <Box className="flex shrink-0">{getFileIcon(attachment.fileName)}</Box>
                {attachment.fileName}
              </a>
            ))}
          </Box>
        );
      } else {
        return (
          <Box className="flex items-center text-gray-400">
            <Text>{t("No Attachments have been added to this ticket")}</Text>
          </Box>
        );
      }
    } else if (id == "visibility") {
      return (
        <Box className="flex flex-col">
          <Box className="flex items-center justify-between">
            <Box className="font-medium text-sm">{t("Visible to all portal users")}</Box>
            {showLoading.visibleToPortalUsers ? (
              <CircularProgress size={"1.25rem"} />
            ) : (
              <ToggleSwitchComponent
                checked={_.get(ticketData, "portal.visibility", false)}
                onChange={(args: React.ChangeEvent<HTMLInputElement>) =>
                  handleVisibiltyChange(args, "visibleToPortalUsers")
                }
              />
            )}
          </Box>
          <Box className="flex items-center justify-between">
            <Box className="font-medium text-sm">{t("Visible to Org users")}</Box>
            {showLoading.visibleToOrgUsers ? (
              <CircularProgress size={"1.25rem"} />
            ) : (
              <ToggleSwitchComponent
                checked={_.get(ticketData, "organization.visibility", false)}
                onChange={(args: React.ChangeEvent<HTMLInputElement>) =>
                  handleVisibiltyChange(args, "visibleToOrgUsers")
                }
              />
            )}
          </Box>
        </Box>
      );
    } else {
      return "";
    }
  };

  const sideBarMapper = [
    ...(isBugTicket && canViewSla
      ? [{ id: "sla", icon: "SLA", name: t("SLA"), content: sideBarContent({ id: "sla" }) }]
      : []),
    { id: "details", icon: "details", name: t("Details"), content: sideBarContent({ id: "details" }) },
    {
      id: "attachments",
      icon: "smallAttachmentIcon",
      name: t("Attachments"),
      content: sideBarContent({ id: "attachments" }),
    },
    ...(canManageVisibility
      ? [
          {
            id: "visibility",
            icon: "VisibilityIcon",
            name: t("Visibility"),
            content: sideBarContent({ id: "visibility" }),
          },
        ]
      : []),
  ];

  const getTicketKey = () => (
    <Box className="flex items-center gap-2">
      <IconComponent
        name={issueTypeIcons[_.get(ticketData, "fields.issueType.id")!] ?? "askAQuestion"}
        width="1.25rem"
        height="1.25rem"
      />
      {ticketData.jiraTicketKey}
    </Box>
  );

  const handleCloseCommentEditor = () => {
    setShowCommentEditor(false);
    setIsInternalNote(false);
    setCommentValue("");
    setCommentFiles([]);
    if (richTextEditorRef.current) {
      richTextEditorRef.current.destroy();
    }
  };

  const handleFileOpen = (fileId: string) => {
    const fileUrl = `${getBackendUrl()}/jira/getAttachmentContent/${fileId}`;
    globalThis.open(fileUrl, "_blank");
  };

  const openAttachmentsModal = (attachments: { fileName: string; fileId: string }[]) => {
    setAttachmentsViewState({ state: true, attachments });
  };

  const closeAttachmentsModal = () => {
    setAttachmentsViewState({ state: false, attachments: [] });
  };

  const uploadFiles = async (filesToUpload: File[] = commentFiles): Promise<UploadFilesResult> => {
    const formData = new FormData();
    const ticketData = {
      action: "postCommentAttachment",
      ticketId,
    };

    for (const file of filesToUpload || []) {
      formData.append("uploadFiles", file);
    }
    formData.append("value", JSON.stringify(ticketData));
    if (filesToUpload.length) {
      const res = await jiraController(formData);
      return res;
    }
    return {
      data: {
        status: true,
        result: [],
      },
    };
  };

  const handlePostComment = async () => {
    setShowLoading((prev) => ({ ...prev, comment: true }));
    const fileRes = await uploadFiles();
    if (!fileRes.data.status) {
      dispatch(
        showToastNotification({
          // KNOWN GAP, preserved verbatim: the raw server string is shown untranslated here, unlike the
          // sibling handlers that wrap it in `untranslated(...)`. Asserted, because a call would emit.
          content: fileRes.data.errorMessage as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
      setShowLoading((prev) => ({ ...prev, comment: false }));
      return;
    }
    const imageTempKeys = extractTempKeysFromHtml(commentValue);
    const html = replaceBase64WithTempKeys(
      commentValue,
      (tempKey) => `${getBackendUrl()}/fileSystemServer/getCommentImage/${tempKey}?type=jira`,
    );
    const finalComment = convertHtmlToAdf(html);
    const response = await jiraController({
      value: {
        action: "postComment",
        comment: finalComment,
        isInternalNote,
        commentString: html,
        ticketId,
        attachments: fileRes.data.result,
        imageTempKeys,
      },
    });
    if (response.data.status) {
      handleCloseCommentEditor();
      fetchTicketData();
      dispatch(
        showToastNotification({
          content: t("Comment added successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setShowLoading((prev) => ({ ...prev, comment: false }));
  };

  const handleCommentChange = (value: string) => {
    setCommentValue(value ?? "");
  };

  const handleFileUpload = (args: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(args.target.files || []) as File[];
    setCommentFiles((prev) => [...prev, ...files]);
  };

  const handleFileRemove = (indexToRemove: number) => {
    setCommentFiles((prevFiles) => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  const handleReplyFileUpload = (args: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(args.target.files || []) as File[];
    setReplyFiles((prev) => [...prev, ...files]);
  };

  const handleReplyFileRemove = (indexToRemove: number) => {
    setReplyFiles((prevFiles) => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  const exitEditMode = () => {
    setEditingCommentId(null);
    setEditCommentValue("");
    setEditCommentExistingAttachments([]);
    setEditCommentPendingFiles([]);
  };

  const handleToggleReaction = async (commentId: string) => {
    const currentUserId = userDetails?._id;
    if (!currentUserId) return;
    setTicketData((prev) => {
      // KNOWN BUG, preserved verbatim: `activity` is dereferenced with no guard, though every other
      // reader of it in this file defaults to `[]`. Erased assertion; a guard would change behaviour.
      const updatedActivity = prev.activity!.map((item) => {
        if (item.commentId !== commentId) return item;
        const reactions = item.reactions || [];
        const hasReacted = reactions.some((r) => r._id?.toString() === currentUserId.toString());
        if (hasReacted) {
          return {
            ...item,
            reactions: reactions.filter((r) => r._id?.toString() !== currentUserId.toString()),
          };
        }
        return {
          ...item,
          reactions: [...reactions, { _id: currentUserId, fullName: userDetails.fullName }],
        };
      });
      return { ...prev, activity: updatedActivity };
    });
    const res = await jiraController({ value: { action: "toggleReaction", ticketId, commentId } });
    if (!res.data.status) {
      await fetchTicketData();
    }
  };

  const handleSaveEdit = async (commentId: string) => {
    const fileRes = await uploadFiles(editCommentPendingFiles);
    if (!fileRes?.data?.status) {
      dispatch(
        showToastNotification({
          content: t("Failed to upload attachments"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }
    const newAttachmentRefs: { fileName: string; fileId: string }[] = fileRes.data.result || [];
    const imageTempKeys = extractTempKeysFromHtml(editCommentValue);
    const html = replaceBase64WithTempKeys(
      editCommentValue,
      (tempKey) => `${getBackendUrl()}/fileSystemServer/getCommentImage/${tempKey}`,
    );
    const finalAttachments = [...editCommentExistingAttachments, ...newAttachmentRefs];

    const response = await jiraController({
      value: {
        action: "editComment",
        ticketId,
        commentId,
        commentString: html,
        imageTempKeys,
        attachments: finalAttachments,
      },
    });
    if (response.data.status) {
      exitEditMode();
      fetchTicketData();
      dispatch(showToastNotification({ content: t("Comment updated successfully."), type: TOAST_TYPE_SUCCESS }));
    } else {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const handleDeleteComment = (commentId: string) => {
    setDeleteConfirmCommentId(commentId);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmCommentId) return;
    const response = await jiraController({
      value: { action: "deleteComment", ticketId, commentId: deleteConfirmCommentId },
    });
    setDeleteConfirmCommentId(null);
    if (response.data.status) {
      fetchTicketData();
      dispatch(showToastNotification({ content: t("Comment deleted."), type: TOAST_TYPE_SUCCESS }));
    } else {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const handlePostReply = async (replyToId: string) => {
    setShowLoading((prev) => ({ ...prev, comment: true }));
    const fileRes = await uploadFiles(replyFiles);
    if (!fileRes.data.status) {
      dispatch(
        showToastNotification({ content: fileRes.data.errorMessage as TranslatedString, type: TOAST_TYPE_ERROR }),
      );
      setShowLoading((prev) => ({ ...prev, comment: false }));
      return;
    }
    const imageTempKeys = extractTempKeysFromHtml(replyValue);
    const html = replaceBase64WithTempKeys(
      replyValue,
      (tempKey) => `${getBackendUrl()}/fileSystemServer/getCommentImage/${tempKey}?type=jira`,
    );

    const response = await jiraController({
      value: {
        action: "postComment",
        commentString: html,
        isInternalNote: replyingToIsInternalNote,
        ticketId,
        replyToId,
        attachments: fileRes.data.result,
        imageTempKeys,
      },
    });

    if (response.data.status) {
      setReplyingToCommentId(null);
      setReplyValue("");
      setReplyFiles([]);
      fetchTicketData();
      dispatch(showToastNotification({ content: t("Reply added successfully."), type: TOAST_TYPE_SUCCESS }));
    } else {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage as TranslatedString,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    setShowLoading((prev) => ({ ...prev, comment: false }));
  };

  const renderCommentComposer = ({
    value,
    onChange,
    files,
    fileInputId,
    onFilesSelected,
    onRemoveFile,
    onSubmit,
    submitLabel,
    submitDisabled,
    onCancel,
  }: {
    value: string;
    onChange: (v: string) => void;
    files: File[];
    fileInputId: string;
    onFilesSelected: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onRemoveFile: (index: number) => void;
    onSubmit: () => void;
    submitLabel: string;
    submitDisabled: boolean;
    onCancel: () => void;
  }) => (
    <Box className="flex flex-col gap-4">
      <SimpleEditor
        value={value}
        onChange={onChange}
        showToolbar
        showCharCount
        maxLength={5000}
        height="200"
        allowImageUpload
        uploadType="jira"
      />
      <Box className="flex flex-wrap items-center gap-2">
        <ButtonComponent
          buttonType={BUTTON_TYPES.BORDER_BUTTON}
          onClick={() => document?.getElementById(fileInputId)?.click()}
          sx={{ borderRadius: "1rem", borderColor: "#d1dbeb", color: "#192048" }}
        >
          <Box className="flex items-center gap-2">
            <IconComponent name="smallAttachmentIcon" width="1rem" height="1rem" />
            {t("Add Attachment")}
          </Box>
        </ButtonComponent>
        <input
          accept=".doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/png,image/jpg,image/jpeg,video/*"
          type="file"
          multiple
          id={fileInputId}
          onChange={onFilesSelected}
          style={{ display: "none" }}
        />
        {files.map((file, index) => (
          <Box key={index} className="flex items-center border p-1 border-button_light w-fit rounded-lg gap-3 bg-white">
            {getFileIcon(file.name)}
            {file.name}
            <IconComponent className="cursor-pointer" onClick={() => onRemoveFile(index)} name="cross" />
          </Box>
        ))}
      </Box>
      <Box className="flex gap-3 items-center">
        <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} disabled={submitDisabled} onClick={onSubmit}>
          <Box className="flex items-center gap-2">
            <IconComponent name="send" height="1rem" width="1rem" />
            {submitLabel}
          </Box>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onCancel}>
          {t("Cancel")}
        </ButtonComponent>
      </Box>
    </Box>
  );

  const renderReplyEditor = (replyToId: string) => (
    <Box className="ms-8 mt-2 border-s-2 border-button_light ps-3">
      {renderCommentComposer({
        value: replyValue,
        onChange: (v: string) => setReplyValue(v ?? ""),
        files: replyFiles,
        fileInputId: "replyCommentFileUpload",
        onFilesSelected: handleReplyFileUpload,
        onRemoveFile: handleReplyFileRemove,
        onSubmit: () => handlePostReply(replyToId),
        submitLabel: t("Reply in Thread"),
        submitDisabled: isHtmlContentEmpty(replyValue) || showLoading.comment,
        onCancel: () => {
          setReplyingToCommentId(null);
          setReplyValue("");
          setReplyFiles([]);
        },
      })}
    </Box>
  );

  const renderCommentBody = (comment: JiraCommentActivity) => {
    if (comment.recordStatus === recordStatus.DELETED) {
      return <Box className="text-sm text-gray-400 italic ps-3">{"This comment was deleted"}</Box>;
    }
    if (editingCommentId === comment.commentId) {
      return (
        <Box className="flex flex-col gap-2">
          <SimpleEditor
            key={`edit-${comment.commentId}`}
            value={editCommentValue}
            onChange={(v: string) => setEditCommentValue(v ?? "")}
            showToolbar={true}
            showCharCount={true}
            maxLength={5000}
            height="150"
            allowImageUpload={true}
            uploadType="jira"
          />
          <Box className="flex gap-3">
            <ButtonComponent
              buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              disabled={isHtmlContentEmpty(editCommentValue)}
              onClick={() => handleSaveEdit(comment.commentId)}
            >
              <Text>{t("Save")}</Text>
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={exitEditMode}>
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </Box>
        </Box>
      );
    }
    return (
      <Box className="ps-3">
        <SimpleEditor
          key={`view-${comment.commentId}`}
          value={comment.comment}
          editable={false}
          showToolbar={false}
          showCharCount={false}
        />
        {comment.editedOn && (
          <Box className="text-xs text-gray-400 ps-1 mt-1">
            <Text>{"(edited)"}</Text>
          </Box>
        )}
      </Box>
    );
  };

  const statusClause = (
    fromName: string | undefined,
    fromId: string | undefined,
    toName: string | undefined,
    toId: string | undefined,
  ) => (
    <Trans
      i18nKey="activityStatusFromTo"
      values={{ fromStatus: fromName, toStatus: toName }}
      components={{
        statusFrom: <ActivityStatusChip statusId={fromId} />,
        statusTo: <ActivityStatusChip statusId={toId} />,
      }}
    />
  );

  const renderActivityDescription = (item: JiraActivity) => {
    const clauses: React.ReactNode[] = [];
    if (item.type === "status") {
      clauses.push(statusClause(item.fromStatusName, item.fromStatusId, item.toStatusName, item.toStatusId));
    } else if (item.type === "severity") {
      clauses.push(
        <Trans
          i18nKey="changedSeverityFromTo"
          values={{ fromSeverity: item.fromSeverity?.label || "-", toSeverity: item.toSeverity?.label || "-" }}
          components={{ strong: <strong /> }}
        />,
      );
      if (isCurrentUserWhiteListed && (item.fromPriority || item.toPriority)) {
        clauses.push(
          <Trans
            i18nKey="changedPriorityFromTo"
            values={{ fromPriority: item.fromPriority?.label || "-", toPriority: item.toPriority?.label || "-" }}
            components={{ strong: <strong /> }}
          />,
        );
      }
    } else if (item.type === "typeChange") {
      clauses.push(
        <Trans
          i18nKey="activityTypeFromTo"
          values={{ fromType: item.fromTypeName, toType: item.toTypeName }}
          components={{
            typeFrom: <ActivityTypeChip typeId={item.fromTypeId} />,
            typeTo: <ActivityTypeChip typeId={item.toTypeId} />,
          }}
        />,
      );
      if (item.fromStatusId !== item.toStatusId) {
        clauses.push(statusClause(item.fromStatusName, item.fromStatusId, item.toStatusName, item.toStatusId));
      }
      if (item.toImpact?.id && item.fromImpact?.id !== item.toImpact?.id) {
        clauses.push(
          <Trans
            i18nKey="activityImpactSetTo"
            values={{ toImpact: item.toImpact?.label || "-" }}
            components={{ strong: <strong /> }}
          />,
        );
      }
      if (
        (canViewSla || customSupportSlaEnabled) &&
        item.toSeverity?.id &&
        item.fromSeverity?.id !== item.toSeverity?.id
      ) {
        clauses.push(
          <Trans
            i18nKey="changedSeverityFromTo"
            values={{ fromSeverity: item.fromSeverity?.label || "-", toSeverity: item.toSeverity?.label || "-" }}
            components={{ strong: <strong /> }}
          />,
        );
      }
      if (canViewSla && item.toPriority?.id && item.fromPriority?.id !== item.toPriority?.id) {
        clauses.push(
          <Trans
            i18nKey="changedPriorityFromTo"
            values={{ fromPriority: item.fromPriority?.label || "-", toPriority: item.toPriority?.label || "-" }}
            components={{ strong: <strong /> }}
          />,
        );
      }
    }
    return (
      <>
        {`${t("activityChanged")} `}
        {clauses.map((clause, i) => (
          <React.Fragment key={i}>
            {i > 0 && (i === clauses.length - 1 ? " and " : ", ")}
            {clause}
          </React.Fragment>
        ))}
      </>
    );
  };

  const renderActivityTimelineEntry = (item: JiraActivity, index: number, isLast: boolean) => (
    <Box key={item.createdOn ?? index} className="flex gap-4 items-start">
      <Box className="w-40 shrink-0 h-8 flex items-center text-sm text-[#666a84]">
        {convertDate(item.createdOn, "dateForTooltip")}
      </Box>
      <Box className="flex flex-col items-center shrink-0 self-stretch">
        <Box className="h-8 flex items-center">
          <Box className="h-2.5 w-2.5 rounded-full bg-stroke_primary_light" />
        </Box>
        {!isLast && <Box className="w-px flex-1 bg-stroke_primary_light" />}
      </Box>
      <Box className="flex flex-wrap items-start gap-2 pb-6 flex-1 min-w-0 min-h-8">
        <Box className="flex h-8 items-center shrink-0">
          <UserNameTagComponent id={item.user?._id} fullName={item.user?.fullName} />
        </Box>
        <Box className="break-words leading-8 flex-1 min-w-0">{renderActivityDescription(item)}</Box>
      </Box>
    </Box>
  );

  const activityContent = () => {
    const activities = [...(ticketData?.activity || [])]
      .filter(
        (a) =>
          a.type === "status" ||
          (a.type === "severity" && (canViewSla || customSupportSlaEnabled)) ||
          a.type === "typeChange",
      )
      .sort((a, b) => b.createdOn - a.createdOn);
    return (
      <Box className="flex flex-col p-4 w-full">
        {activities.length === 0 ? (
          <Box className="flex items-center text-gray-400">
            <Text>{"No activity yet"}</Text>
          </Box>
        ) : (
          activities.map((item, index: number) =>
            renderActivityTimelineEntry(item, index, index === activities.length - 1),
          )
        )}
      </Box>
    );
  };

  const rightSideBar = () => {
    const allIds = sideBarMapper.map((s) => s.id);
    const openIds = openSidebarSections ?? allIds;
    const toggleSection = (id: string) =>
      setOpenSidebarSections(openIds.includes(id) ? openIds.filter((x: string) => x !== id) : [...openIds, id]);

    const accordionSx = {
      "& .MuiAccordionSummary-root": { width: "100%", padding: "0 1rem" },
      "& .MuiAccordionDetails-root": {
        height: "100%",
        width: "100%",
        padding: "0 1rem 1rem 1rem",
        transition: "opacity 0.3s ease, transform 0.3s ease",
        opacity: 0,
        transform: "translateY(-0.25rem)",
      },
    };

    return (
      <Box className="flex w-full h-full min-h-0">
        <Box className="flex flex-col flex-1 min-w-0 overflow-y-auto">
          {sideBarMapper.map((section) => (
            <Accordion
              key={section.id}
              expanded={openIds.includes(section.id)}
              onChange={() => toggleSection(section.id)}
              sx={accordionSx}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {sideBarHeader({ icon: section.icon, name: section.name })}
              </AccordionSummary>
              <AccordionDetails>{section.content}</AccordionDetails>
            </Accordion>
          ))}
        </Box>
        <Box className="flex flex-col items-center gap-2 border-s border-stroke_primary_light px-2 py-4 shrink-0">
          {sideBarMapper.map((section) => (
            <Box
              key={section.id}
              component="button"
              aria-label={section.name}
              onClick={() => setOpenSidebarSections([section.id])}
              className="flex items-center justify-center p-2 cursor-pointer bg-transparent border-0 outline-none focus:outline-none"
            >
              <IconComponent name={section.icon} width="1.25rem" height="1.25rem" />
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const startEditComment = (comment: JiraCommentActivity) => {
    setEditingCommentId(comment.commentId);
    setEditCommentValue(comment.comment);
    setEditCommentExistingAttachments(
      (comment.attachments || []).map((a: JiraAttachmentRef) => ({
        fileName: a.fileName,
        fileId: a.fileId,
      })),
    );
    setEditCommentPendingFiles([]);
  };

  const renderCommentCard = (
    comment: JiraCommentActivity,
    { isReply = false, replyCount = 0 }: { isReply?: boolean; replyCount?: number } = {},
  ) => {
    const isAuthor = comment.user?._id?.toString() === userDetails?._id?.toString();
    const isDeleted = comment.recordStatus === recordStatus.DELETED;
    const thumbsUp = comment.reactions || [];
    const hasReacted = thumbsUp.some((r) => r._id?.toString() === userDetails?._id?.toString());
    const attachmentCount = comment.attachments?.length || 0;

    return (
      <Box className="flex flex-col gap-3">
        <Box className="flex items-start justify-between gap-2">
          <Box className="flex items-center gap-2 flex-wrap min-w-0">
            <UserNameTagComponent id={comment.user?._id} fullName={comment.user?.fullName} />
            {comment.isInternalNote && (
              <Box
                component="span"
                className="inline-flex items-center rounded-md bg-bg_warning px-2 py-0.5 text-xs font-semibold text-primary_text whitespace-nowrap"
              >
                {t("Internal Comment")}
              </Box>
            )}
          </Box>
          <Box className="flex items-center gap-2 shrink-0">
            <Box className="text-sm text-[#666a84] whitespace-nowrap">
              {convertDate(comment.createdOn, "dateForTooltip")}
            </Box>
            {isAuthor && !isDeleted && (
              <ButtonComponent
                iconButton
                iconName="bi_three-dots_verti"
                buttonType={BUTTON_TYPES.TEXT_BUTTON}
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  setCommentMenuAnchor(e.currentTarget);
                  setMenuComment(comment);
                }}
              />
            )}
          </Box>
        </Box>

        {renderCommentBody(comment)}

        {!isDeleted && editingCommentId !== comment.commentId && (
          <Box className="flex items-center gap-6 ps-1">
            <Tooltip title={thumbsUp.map((r) => r.fullName).join(", ") || ""} arrow>
              <button
                className={`flex items-center gap-1 text-sm ${hasReacted ? "text-blue-500" : "text-gray-500 hover:text-blue-500"}`}
                onClick={() => handleToggleReaction(comment.commentId)}
              >
                <IconComponent name="acknowledge" width="1.1rem" height="1.1rem" />
                {thumbsUp.length > 0 && <span className="text-[#192048] font-semibold">{thumbsUp.length}</span>}
              </button>
            </Tooltip>
            {!isReply && (
              <button
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-blue-500"
                onClick={() => {
                  if (replyingToCommentId === comment.commentId) {
                    setReplyingToCommentId(null);
                  } else {
                    setReplyingToCommentId(comment.commentId);
                    setReplyingToIsInternalNote(comment.isInternalNote || false);
                  }
                }}
              >
                <IconComponent name="Reply" width="1.1rem" height="1.1rem" />
                {replyCount > 0 && <span className="text-[#192048] font-semibold">{replyCount}</span>}
              </button>
            )}
            {attachmentCount > 0 && (
              <button
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-blue-500"
                onClick={() => openAttachmentsModal(comment.attachments!)}
              >
                <IconComponent name="smallAttachmentIcon" width="1.1rem" height="1.1rem" />
                <span className="text-[#192048] font-semibold">{attachmentCount}</span>
              </button>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderCommentsList = () => {
    // The predicate is what makes `commentId`/`comment` known-present on these entries; every other
    // kind of activity entry leaves them undefined.
    const comments = (ticketData?.activity || []).filter((a): a is JiraCommentActivity => a.type === "comment");
    const repliesMap: Record<string, JiraCommentActivity[]> = {};
    for (const c of comments) {
      if (c.replyToId) {
        if (!repliesMap[c.replyToId]) repliesMap[c.replyToId] = [];
        repliesMap[c.replyToId]!.push(c);
      }
    }
    const topLevel = comments
      .filter((c) => !c.replyToId)
      .filter((c) => (c.isInternalNote && isCurrentUserWhiteListed) || !c.isInternalNote)
      .sort((a, b) => b.createdOn - a.createdOn);

    return (
      <Box className="flex flex-col gap-6">
        {topLevel.map((comment, index: number) => {
          const replies = (repliesMap[comment.commentId] || [])
            .filter((r) => (r.isInternalNote && isCurrentUserWhiteListed) || !r.isInternalNote)
            .sort((a, b) => a.createdOn - b.createdOn);
          return (
            <Box
              key={comment.commentId || index}
              className={index !== topLevel.length - 1 ? "border-b border-stroke_primary_light pb-4" : ""}
            >
              <Box
                className={`flex flex-col gap-4 ${comment.isInternalNote ? "bg-light_background rounded-lg p-3" : ""}`}
              >
                {renderCommentCard(comment, { replyCount: replies.length })}
                {replies.map((reply, rIndex: number) => (
                  <Box key={reply.commentId || rIndex} className="ms-8">
                    {renderCommentCard(reply, { isReply: true })}
                  </Box>
                ))}
                {replyingToCommentId === comment.commentId && renderReplyEditor(comment.commentId)}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  const commentsContent = () => (
    <Box className="flex p-4 flex-col gap-6">
      {showCommentEditor ? (
        <Box
          className={`relative flex flex-col gap-4 p-4 rounded-lg border border-stroke_primary_light ${
            isInternalNote ? "bg-light_background" : "bg-white"
          }`}
        >
          {showLoading.comment && (
            <Box className="absolute inset-0 bg-white/50 backdrop-blur-sm flex justify-center items-center z-20 rounded-lg">
              <CircularProgress />
            </Box>
          )}
          {renderCommentComposer({
            value: commentValue,
            onChange: handleCommentChange,
            files: commentFiles,
            fileInputId: "activityCommentFileUpload",
            onFilesSelected: handleFileUpload,
            onRemoveFile: handleFileRemove,
            onSubmit: handlePostComment,
            submitLabel: t("Send"),
            submitDisabled: isHtmlContentEmpty(commentValue),
            onCancel: handleCloseCommentEditor,
          })}
        </Box>
      ) : (
        <Box className="flex items-center px-4 py-6 gap-4 border border-stroke_primary_light rounded-lg">
          <UserNameTagComponent id={userDetails?._id} showAvatarOnly />
          <ButtonComponent
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            onClick={() => {
              setIsInternalNote(false);
              setShowCommentEditor(true);
            }}
          >
            <Box className="flex items-center gap-2">
              <IconComponent name="add" width="1rem" height="1rem" />
              {t("Add Comment")}
            </Box>
          </ButtonComponent>
          {isCurrentUserWhiteListed && (
            <ButtonComponent
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              onClick={() => {
                setIsInternalNote(true);
                setShowCommentEditor(true);
              }}
            >
              <Box className="flex items-center gap-2">
                <IconComponent name="add" width="1rem" height="1rem" />
                {t("Add Internal Comment")}
              </Box>
            </ButtonComponent>
          )}
        </Box>
      )}
      {renderCommentsList()}
      <Menu anchorEl={commentMenuAnchor} open={Boolean(commentMenuAnchor)} onClose={() => setCommentMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            if (menuComment) startEditComment(menuComment);
            setCommentMenuAnchor(null);
          }}
        >
          <Box className="flex items-center gap-2">
            <IconComponent name="edit" width="1rem" height="1rem" />
            {t("Edit")}
          </Box>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuComment) handleDeleteComment(menuComment.commentId);
            setCommentMenuAnchor(null);
          }}
        >
          <Box className="flex items-center gap-2">
            <IconComponent name="delete" width="1rem" height="1rem" />
            {t("Delete")}
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );

  const getContent = () => (
    <Box className="flex flex-col w-full h-full overflow-hidden">
      <Box className="flex w-full flex-1 min-h-0 overflow-hidden p-4 gap-4">
        <Box className="flex flex-col flex-[2] min-w-0 overflow-y-auto border border-stroke_primary_light rounded-lg">
          <Box className="sticky top-0 z-10 bg-white flex items-center border-b border-stroke_primary_light px-4 py-2 gap-2 min-h-20">
            <IconComponent
              className="cursor-pointer"
              name="BackwardArrow"
              onClick={() => globalThis.history.back()}
              height="1.25rem"
              width="1.25rem"
            />
            {getTicketKey()}
          </Box>
          <Box className="flex items-start flex-col w-full">
            <Box className="flex items-center w-full">
              <Text
                className="px-4 py-2 text-base font-semibold shrink-0 w-36"
                sx={{ whiteSpace: "nowrap", overflow: "visible", textOverflow: "clip" }}
              >
                {t("Summary")}
              </Text>
              <Box className="px-4 py-2 flex-1 min-w-0 break-words">{_.get(ticketData, "fields.summary")}</Box>
            </Box>
            <Box className="flex items-center w-full">
              <Text
                className="px-4 py-2 text-base font-semibold shrink-0 w-36"
                sx={{ whiteSpace: "nowrap", overflow: "visible", textOverflow: "clip" }}
              >
                {t("Description")}
              </Text>
              <Box className="px-4 py-2 flex-1 min-w-0 break-words">
                <SimpleEditor
                  value={sanitizeHtml(_.get(ticketData, "fields.description"))}
                  editable={false}
                  showToolbar={false}
                  showCharCount={false}
                />
              </Box>
            </Box>
          </Box>
          <Box
            className="flex flex-col flex-1 m-4 border border-stroke_primary_light rounded-lg"
            sx={{ "& .MuiTabs-root": { px: 3, borderBottom: "1px solid #C6D8EC" } }}
          >
            <TabBarComponent
              selectedItem={0}
              items={[
                { header: { text: t("Comments") }, content: () => commentsContent() },
                { header: { text: t("Activity") }, content: () => activityContent() },
              ]}
            />
          </Box>
        </Box>
        <Box className="flex flex-col flex-1 min-w-0 overflow-hidden border border-stroke_primary_light rounded-lg">
          {rightSideBar()}
        </Box>
      </Box>
    </Box>
  );

  const renderTicketAccessError = () => (
    <Box className="w-full h-full flex items-center justify-center overflow-auto">
      {accessError === errorCodes.JIRA_TICKET_NO_ACCESS ? (
        getNoAccessPage("Ticket")
      ) : (
        <IconComponent name="404Illustration" width="22.5625rem" height="19rem" />
      )}
    </Box>
  );

  return (
    <>
      {accessError ? (
        renderTicketAccessError()
      ) : ticketData.jiraTicketKey ? (
        getContent()
      ) : (
        <Box className="flex w-full h-full justify-center items-center">
          <CircularProgress />
        </Box>
      )}
      <ModalStateDialogComponent
        width="50%"
        height="50%"
        isVisible={attachmentsViewState.state}
        modelContent={
          <Box className="flex flex-col gap-2 overflow-auto">
            {attachmentsViewState.attachments.map((file, index) => (
              <Box
                key={index}
                component="button"
                onClick={() => handleFileOpen(file.fileId)}
                className="flex items-center gap-2 w-full text-start border rounded-lg px-3 py-2 bg-white text-[#192048] text-sm font-semibold cursor-pointer transition-colors hover:bg-light_background"
                style={{ borderColor: "#d1dbeb" }}
              >
                <Box className="flex shrink-0">{getFileIcon(file.fileName)}</Box>
                <Box className="min-w-0 break-words">{file.fileName}</Box>
              </Box>
            ))}
          </Box>
        }
        footerContent={<></>}
        headerTitle={t("Attachments")}
        onCancel={closeAttachmentsModal}
      />
      <ModalStateDialogComponent
        isVisible={!!deleteConfirmCommentId}
        headerTitle={t("Delete comment?")}
        modelContent={<Text>{t("Delete this comment?")}</Text>}
        footerContent={
          <Box className="flex gap-3">
            <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={handleConfirmDelete}>
              {t("Delete")}
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={() => setDeleteConfirmCommentId(null)}>
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </Box>
        }
        onCancel={() => setDeleteConfirmCommentId(null)}
      />
      <ChangeTicketTypeModal
        isVisible={showChangeTypeModal}
        // `ticketData` starts as `{}` and only gains `_id` once the fetch lands, so the modal's
        // required `_id` is asserted here rather than made optional on its own Ticket type.
        ticket={ticketData as typeof ticketData & { _id: string }}
        initialTargetTypeId={changeTypeTargetId}
        onCancel={() => setShowChangeTypeModal(false)}
        onConfirm={handleChangeTypeConfirm}
        loading={changeTypeLoading}
        customSupportSlaEnabled={customSupportSlaEnabled}
      />
      <ModalStateDialogComponent
        width="50%"
        isVisible={!!confirmTransition}
        headerTitle={t("Confirm Status Change")}
        modelContent={
          <Box className="flex flex-col gap-2">
            <Box className="text-sm text-text_light">{t("This comment will be shared with the reporter.")}</Box>
            <SimpleEditor
              value={confirmComment}
              onChange={(v: string) => setConfirmComment(v ?? "")}
              showToolbar
              showCharCount
              maxLength={5000}
              height="200"
              allowImageUpload={false}
              uploadType="jira"
            />
          </Box>
        }
        footerContent={
          <Box className="flex gap-3">
            <ButtonComponent
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              loading={confirmLoading}
              disabled={isHtmlContentEmpty(confirmComment) || confirmLoading}
              onClick={submitConfirmTransition}
            >
              <Box className="flex items-center gap-2">{t("Post & Update")}</Box>
            </ButtonComponent>
            <ButtonComponent
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              onClick={() => {
                setConfirmTransition(null);
                setConfirmComment("");
              }}
            >
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </Box>
        }
        onCancel={() => {
          setConfirmTransition(null);
          setConfirmComment("");
        }}
      />
      <ModalStateDialogComponent
        width="50%"
        isVisible={!!confirmSeverityChange}
        headerTitle={t("Confirm Severity Change")}
        modelContent={
          <Box className="flex flex-col gap-2">
            <Box className="text-sm text-text_light">
              {t("Please add a reason for changing the severity of this ticket.")}
            </Box>
            <SimpleEditor
              value={severityJustification}
              onChange={(v: string) => setSeverityJustification(v ?? "")}
              showToolbar
              showCharCount
              maxLength={5000}
              height="200"
              allowImageUpload={false}
              uploadType="jira"
            />
          </Box>
        }
        footerContent={
          <Box className="flex gap-3">
            <ButtonComponent
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              loading={severityJustificationLoading}
              disabled={isHtmlContentEmpty(severityJustification) || severityJustificationLoading}
              onClick={submitConfirmSeverityChange}
            >
              <Box className="flex items-center gap-2">{t("Confirm")}</Box>
            </ButtonComponent>
            <ButtonComponent
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              onClick={() => {
                setConfirmSeverityChange(null);
                setSeverityJustification("");
              }}
            >
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
          </Box>
        }
        onCancel={() => {
          setConfirmSeverityChange(null);
          setSeverityJustification("");
        }}
      />
    </>
  );
};
export default TicketDetails;
